import { useEffect, useState, useCallback } from "react"
import { Curriculum, Course } from "../../../../types/dataModel"
import useFetch from "../../../../hooks/useFetch"
import { API, CURRICULUM_TYPE } from "../../../../common/Constants"
import PreLoader from "../../../../core/common/preLoader"
import { getAPIHeaders, isNotEmpty, toDateFromEpoch } from "../../../../common/Utils"
import CommonSelect from "../../../../core/common/commonSelect"
import { DatePicker } from "antd"
import dayjs from 'dayjs';
import FormFooter from "../../../../core/common/formFooter"
import { useAccess } from "../../../../hooks/accessContext"
import CommonSelectV2 from "../../../../core/common/commonSelectV2"


interface Props {
    curriculum: Curriculum
    onSuccess: ()=> void
}

const ModifyCurriculum = ({curriculum, onSuccess}:Props) => {
    const {hasAcademicMgntEdit} = useAccess()
    const [formData, setFormData] = useState<Curriculum | undefined>(curriculum)
    const [msgBox, setMsgBox] = useState<any>({
      msg: "",
      error: false
    });
    const {isLoading, error, data, status, callFetch } = useFetch();
    const [courseList, setCourseList] = useState<Course[]>([]);
    const [selectedCourses, setSelectedCourses] = useState<any[]>([]);

    const getModalContainer = () => {
      const modalElement = document.getElementById('modal-datepicker');
      return modalElement ? modalElement : document.body; // Fallback to document.body if modalElement is null
    };

    useEffect(()=>{
      setFormData(curriculum)
      if(curriculum?.courses){
        let courseCodes:any = curriculum?.courses.map((ele)=> ele.code)
        if(courseCodes?.length > 0){
          setSelectedCourses(courseCodes)
        }
      } else {
        setSelectedCourses([])
      }
    },[curriculum])

    const fetchCourses = useCallback(async () => {
        try {
            const response = await callFetch(API.COURSE_SEARCH, 'GET', getAPIHeaders(), null, null);
            if (response) {
                setCourseList(response);
            } else {
              setCourseList([]);
            }
        } catch (err) {
            console.error("Failed to fetch courses:", err);
        }
    }, []);

    useEffect(() => {
      fetchCourses();
    }, []);

    const reset = () => {
      resetMsgBox()
    }

    const resetMsgBox = () => {
      setMsgBox({
        msg: "",
        error: false
      })
    }

    useEffect(() => {
              if(data && status && status < 299){
                setMsgBox({msg: "Updated successfully", error: false})
                onSuccess()
              }
    }, [status]);
        
        
    useEffect(() => {
            if(error){
              if(error.response?.data){
                setMsgBox({msg: error.response?.data, error: true})
              } else{
                setMsgBox({msg: 'Something went wrong please try again later.', error: true})
              }
            }
    }, [error]);


    const handleSubmit = (event:any) => {
          event.preventDefault()
          if(!isNotEmpty(formData?.title)){
            setMsgBox({msg: "Please provide title", error: true})
          } else if(!isNotEmpty(formData?.description)){
            setMsgBox({msg: "Please provide description", error: true})
          } else if(!isNotEmpty(formData?.code)){
            setMsgBox({msg: "Please provide code", error: true})
          } else if(!isNotEmpty(formData?.type)){
            setMsgBox({msg: "Please select type", error: true})
          } else if(!formData?.endDate){
            setMsgBox({msg: "Please select end date", error: true})
          } else if(!formData?.startDate){
            setMsgBox({msg: "Please select start date", error: true})
          } else {
            setMsgBox({msg:"",error: false})
            callFetch(API.CURRICULUM,
                  'PUT',
                  getAPIHeaders(),
                  null,
                  {
                     ...formData
                  }
              ).catch((err) => {
                setMsgBox({msg: err.response?.data || "Something went wrong, please try again later.", error: true});
              });
          }
    }

    const handleSaveCourses = (event:any) => {
      event.preventDefault()
      if(!isNotEmpty(formData?.code)){
        setMsgBox({msg: "Please provide curriculum code", error: true})
        return
      }

      setMsgBox({msg:"",error: false})
      
      // Get current course codes from curriculum
      const currentCourseCodes = curriculum?.courses?.map((course) => course.code) || []
      
      // Find courses to add (in selected but not in current)
      const coursesToAdd = selectedCourses.filter((code) => !currentCourseCodes.includes(code))
      
      // Find courses to remove (in current but not in selected)
      const coursesToRemove = currentCourseCodes.filter((code) => !selectedCourses.includes(code))
      
      // Create promises for all operations
      const addPromises = coursesToAdd.map((courseCode) => 
        callFetch(API.CURRICULUM_COURSE,
          'POST',
          getAPIHeaders(),
          null,
          {
            curriculumCode: formData?.code,
            courseCode: courseCode
          }
        )
      )
      
      const removePromises = coursesToRemove.map((courseCode) => 
        callFetch(API.CURRICULUM_COURSE,
          'DELETE',
          getAPIHeaders(),
          null,
          {
            curriculumCode: formData?.code,
            courseCode: courseCode
          }
        )
      )
      
      // Execute all operations
      Promise.all([...addPromises, ...removePromises]).then(()=>{
        setMsgBox({msg: "Updated successfully", error: false})
        onSuccess()
      }).catch((err)=>{
        setMsgBox({msg: err.response?.data || 'Failed to update courses. Please try again.', error: true});
      });
    }

    const renderBasicInfoForm = () => {
      return (
        <div
          className="tab-pane fade show active" 
          id="modify-basic-info"
          role="tabpanel"
          aria-labelledby="modify-info-tab"
          tabIndex={0}
        >
          <div className="modal-body pb-0">
            <div className="row">
              <div className="col-md-12">
                <div className="mb-3">
                  <label className="form-label">Title <span className="text-danger"> *</span></label>
                  <input type="text" className="form-control" 
                  value={formData?.title}
                  onChange={(e)=>{
                    setFormData({
                      ...formData,
                      title: e.target.value
                    })
                  }}
                  disabled={!hasAcademicMgntEdit()}
                  />
                </div>
              </div>
              <div className="col-md-12">
                <div className="mb-3">
                  <label className="form-label">Description <span className="text-danger"> *</span></label>
                  <input type="text" className="form-control"
                      value={formData?.description}
                      onChange={(e)=>{
                          setFormData({
                              ...formData,
                              description: e.target.value
                          })
                      }} 
                      disabled={!hasAcademicMgntEdit()}/>
                </div>
              </div>
              <div className="col-md-6">
                <div className="mb-3">
                  <label className="form-label">Code <span className="text-danger"> *</span></label>
                  <input type="text" className="form-control"
                      value={formData?.code}
                      onChange={(e)=>{
                        setFormData({
                              ...formData,
                              code: e.target.value
                          })
                      }} 
                      disabled/>
                </div>
              </div>
              <div className="col-md-6">
                <div className="mb-3">
                  <label className="form-label">Type <span className="text-danger"> *</span></label>
                  <CommonSelect
                        disabled={!hasAcademicMgntEdit()}
                        className='select'
                        options={CURRICULUM_TYPE}
                        defaultValue={CURRICULUM_TYPE.find((item)=>  item.value === curriculum?.type )}
                        onChange={(value)=>{
                          setFormData({
                              ...formData,
                              type: value
                          })
                    }}
                  />
                </div>
              </div>
              <div className="col-md-6">
                <div className="mb-3">
                  <label className="form-label">
                    Start Date <span className="text-danger"> *</span>
                  </label>
                  <div className="input-icon-end position-relative">
                    <DatePicker
                      disabled={!hasAcademicMgntEdit()}
                      className="form-control datetimepicker"
                      format={{
                        format: "DD-MM-YYYY",
                        type: "mask",
                      }}
                      getPopupContainer={getModalContainer}
                      placeholder="DD-MM-YYYY"
                      value={formData?.startDate ? dayjs(toDateFromEpoch(formData?.startDate)):undefined}
                      onChange={(dt:any) => {
                        setFormData({
                          ...formData,
                          startDate: dt?.toDate()?.getTime()
                        })
                      }}
                      disabledDate={(current) => {
                        return current && current < dayjs().startOf('day');
                      }}
                    />
                    <span className="input-icon-addon">
                      <i className="ti ti-calendar text-gray-7" />
                    </span>
                  </div>
                </div>
              </div>
              <div className="col-md-6">
                <div className="mb-3">
                  <label className="form-label">
                    End Date <span className="text-danger"> *</span>
                  </label>
                  <div className="input-icon-end position-relative">
                    <DatePicker
                      disabled={!hasAcademicMgntEdit()}
                      className="form-control datetimepicker"
                      format={{
                        format: "DD-MM-YYYY",
                        type: "mask",
                      }}
                      getPopupContainer={getModalContainer}
                      placeholder="DD-MM-YYYY"
                      value={formData?.endDate?dayjs(toDateFromEpoch(formData?.endDate)):undefined}
                      onChange={(dt:any) => {
                        setFormData({
                          ...formData,
                          endDate: dt?.toDate()?.getTime()
                        })
                      }}
                      disabledDate={(current) => {
                        return current && 
                          (curriculum?.startDate ? current < dayjs(toDateFromEpoch(curriculum?.startDate)) : current < dayjs().startOf('day'));
                      }}
                    />
                    <span className="input-icon-addon">
                      <i className="ti ti-calendar text-gray-7" />
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div className="modal-footer">
            <FormFooter
            readonly={!hasAcademicMgntEdit()}
            onCancel={reset}
            onSave={handleSubmit}
            error={msgBox.error}
            message={msgBox.msg} />
          </div>
        </div>
      )
    }

    const renderCoursesForm = () => {
      return (
        <div
          className="tab-pane fade"
          id="modify-courses-info"
          role="tabpanel"
          aria-labelledby="modify-courses-tab"
          tabIndex={0}
        >
          <div className="modal-body pb-0">
            <div className="row">
              <div className="col-md-12">
                <div className="mb-3">
                  <label className="form-label">Courses <span className="text-danger"> *</span></label>
                  <CommonSelectV2
                    isMulti
                    className='select'
                    options={parseCoursesToOptions(courseList)}
                    values={parseCoursesToOptions(courseList).filter(
                      (option) => selectedCourses.includes(option.value)
                    )}
                    onChange={(value) => {
                      setSelectedCourses(value)
                    }}
                    disabled={!hasAcademicMgntEdit()}
                    formatOptionLabel={(option: any) => (
                      <div>
                        <h6 className="d-flex align-items-center">
                          {option.label}
                          <span className={`ms-2 badge badge-primary rounded-pill`}>
                            {option.value}
                          </span>
                        </h6>
                      </div>
                    )}
                  />
                </div>
              </div>
            </div>
          </div>
          <div className="modal-footer">
            <FormFooter
              readonly={!hasAcademicMgntEdit()}
              onCancel={reset}
              onSave={handleSaveCourses}
              error={msgBox.error}
              message={msgBox.msg} 
            />
          </div>
        </div>
      )
    }

    const renderForm = () => {
      return (
        <div className="modal-content">
          <div className="modal-header">
            <h4 className="modal-title">Modify Curriculum</h4>
            <button
              type="button"
              className="btn-close custom-btn-close"
              data-bs-dismiss="modal"
              aria-label="Close"
              onClick={reset}
            >
              <i className="ti ti-x" />
            </button>
          </div>
          <form>
            <div className="contact-grids-tab">
              <ul className="nav nav-underline" id="myTab" role="tablist">
                <li className="nav-item" role="presentation">
                  <button
                    className="nav-link active"
                    id="modify-info-tab"
                    data-bs-toggle="tab"
                    data-bs-target="#modify-basic-info"
                    type="button"
                    role="tab"
                    aria-selected="true"
                    onClick={resetMsgBox}
                  >
                    Basic Information
                  </button>
                </li>
                <li className="nav-item" role="presentation">
                  <button
                    className="nav-link"
                    id="modify-courses-tab"
                    data-bs-toggle="tab"
                    data-bs-target="#modify-courses-info"
                    type="button"
                    role="tab"
                    aria-selected="false"
                    onClick={resetMsgBox}
                  >
                    Courses
                  </button>
                </li>
              </ul>
            </div>
            <div className="tab-content" id="myTabContent">
              {renderBasicInfoForm()}
              {renderCoursesForm()}
            </div>
          </form>
        </div>
      )
    }

    return (
      <div className="modal fade" id="edit_curriculum"
        data-bs-backdrop="static"
        data-bs-keyboard="false">
        <PreLoader showLoader={isLoading} />
        <div className="modal-dialog modal-dialog-centered modal-md"
          >
          {renderForm()}
        </div>
      </div>
    )
}

function parseCoursesToOptions (data: Course[]) {
  let opts: any[] = []
  if(data){
    data.forEach((ele)=> opts.push({
      value: ele.code,
      label: ele.title
    }))
  }
  return opts;
}

export default ModifyCurriculum